import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api, { fmtDate } from "@/lib/api";
import { openGoogleConnection } from "@/lib/googleOAuth";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Layout";
import { TwoFactorCard } from "@/components/TwoFactor";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Activity, Mail, Loader2, Plug, RefreshCw, Send, Copy, Globe, HardDrive } from "lucide-react";

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [allowed, setAllowed] = useState([]);
  const [audit, setAudit] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [health, setHealth] = useState(null);
  const [newEmail, setNewEmail] = useState("");
  const [newRole, setNewRole] = useState("operator");
  const [google, setGoogle] = useState({ connected: false, services: {} });
  const [googleConfigured, setGoogleConfigured] = useState(true);
  const [googleConnecting, setGoogleConnecting] = useState(false);
  const [gmailTesting, setGmailTesting] = useState(false);
  const [egress, setEgress] = useState(null);
  const [egressLoading, setEgressLoading] = useState(false);

  const loadEgress = useCallback(() => {
    setEgressLoading(true);
    api.get("/egress-ip").then((r) => setEgress(r.data)).catch(() => setEgress({ ip: null, error: "lookup failed" }))
      .finally(() => setEgressLoading(false));
  }, []);

  const refreshGoogle = useCallback(() => {
    api.get("/oauth/google/connection").then((r) => setGoogle(r.data)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    if (!isAdmin) return;
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/admin/allowed-emails").then((r) => setAllowed(r.data)).catch(() => {});
    api.get("/admin/audit-logs").then((r) => setAudit(r.data)).catch(() => {});
    api.get("/admin/notifications").then((r) => setNotifs(r.data)).catch(() => {});
    api.get("/health").then((r) => setHealth(r.data)).catch(() => {});
    api.get("/oauth/google/config").then((r) => setGoogleConfigured(r.data.configured)).catch(() => setGoogleConfigured(false));
    loadEgress();
    refreshGoogle();
  }, [isAdmin, refreshGoogle, loadEgress]);
  useEffect(() => { load(); }, [load]);

  const copyEgress = async () => {
    if (!egress?.ip) return;
    try {
      await navigator.clipboard.writeText(egress.ip);
      toast.success(t("settings.egressCopied"));
    } catch {
      toast.error(t("settings.egressCopyFailed"));
    }
  };

  const connectGoogle = async () => {
    setGoogleConnecting(true);
    try {
      await openGoogleConnection(["drive", "gmail"]);
      toast.success(t("settings.googleConnected"));
      load();
    } catch (e) {
      toast.error(e.message || t("settings.googleConnectFailed"));
    } finally { setGoogleConnecting(false); }
  };

  const disconnectGoogle = async () => {
    try { await api.delete("/oauth/google/connection"); setGoogle({ connected: false, services: {} }); toast.success(t("settings.googleDisconnected")); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("settings.failed")); }
  };

  const sendGmailTest = async () => {
    setGmailTesting(true);
    try {
      const r = await api.post("/oauth/gmail/test");
      toast.success(t("settings.gmailTestSent", { email: r.data.sent_to }));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("settings.gmailTestFailed")); }
    finally { setGmailTesting(false); }
  };

  const addAllowed = async () => {
    if (!newEmail) return;
    try { await api.post("/admin/allowed-emails", { email: newEmail, role: newRole }); toast.success(t("settings.added")); setNewEmail(""); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("settings.failed")); }
  };
  const removeAllowed = async (email) => {
    try { await api.delete(`/admin/allowed-emails/${encodeURIComponent(email)}`); load(); }
    catch (e) { toast.error(t("settings.failed")); }
  };
  const setUserRole = async (uid, role) => {
    try { await api.put(`/admin/users/${uid}/role`, { role }); toast.success(t("settings.roleUpdated")); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("settings.failed")); }
  };

  return (
    <div data-testid="settings-page">
      <PageHeader title={t("settings.title")} subtitle={t(isAdmin ? "settings.subtitle" : "settings.securitySubtitle")} />
      <div className="p-6 md:p-8">
        <Tabs defaultValue="security">
          <TabsList className="rounded-none bg-secondary h-auto flex-wrap justify-start">
            <TabsTrigger value="security" className="rounded-none" data-testid="tab-security">{t("settings.tabSecurity")}</TabsTrigger>
            {isAdmin && <>
            <TabsTrigger value="users" className="rounded-none" data-testid="tab-users">{t("settings.tabUsers")}</TabsTrigger>
            <TabsTrigger value="whitelist" className="rounded-none" data-testid="tab-whitelist">{t("settings.tabWhitelist")}</TabsTrigger>
            <TabsTrigger value="connections" className="rounded-none" data-testid="tab-connections">{t("settings.tabConnections")}</TabsTrigger>
            <TabsTrigger value="health" className="rounded-none" data-testid="tab-health">{t("settings.tabHealth")}</TabsTrigger>
            <TabsTrigger value="notifications" className="rounded-none" data-testid="tab-notifications">{t("settings.tabNotifications")}</TabsTrigger>
            <TabsTrigger value="audit" className="rounded-none" data-testid="tab-audit">{t("settings.tabAudit")}</TabsTrigger>
            </>}
          </TabsList>

          <TabsContent value="security" className="mt-4">
            <TwoFactorCard />
          </TabsContent>

          {isAdmin && <>
          <TabsContent value="users" className="mt-4">
            <div className="grid-panel overflow-x-auto">
              <table className="w-full text-sm min-w-[600px]">
                <thead><tr className="text-left font-mono text-[10px] uppercase text-muted-foreground border-b border-border"><th className="px-5 py-3">{t("settings.thName")}</th><th className="px-5 py-3">{t("settings.thEmail")}</th><th className="px-5 py-3">{t("settings.thRole")}</th><th className="px-5 py-3">{t("settings.thStatus")}</th></tr></thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.user_id} className="data-grid-row" data-testid={`user-row-${u.user_id}`}>
                      <td className="px-5 py-3">{u.name}</td>
                      <td className="px-5 py-3 font-mono text-xs">{u.email}</td>
                      <td className="px-5 py-3">
                        <Select value={u.role} onValueChange={(r) => setUserRole(u.user_id, r)} disabled={u.user_id === user.user_id}>
                          <SelectTrigger className="rounded-none h-8 w-32" data-testid={`user-role-${u.user_id}`}><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="admin">admin</SelectItem><SelectItem value="operator">operator</SelectItem><SelectItem value="viewer">viewer</SelectItem></SelectContent>
                        </Select>
                      </td>
                      <td className="px-5 py-3"><StatusBadge status={u.status === "active" ? "valid" : "invalid"} label={u.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="connections" className="mt-4">
            <div className="grid-panel p-5" data-testid="google-connect-card">
              <div className="flex items-center gap-2 mb-2">
                <Plug className="h-4 w-4 text-brand" />
                <h3 className="font-display font-bold">{t("settings.googleTitle")}</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-4 max-w-2xl">{t("settings.googleDesc")}</p>
              {!googleConfigured && (
                <p className="font-mono text-[11px] text-destructive border border-destructive/40 bg-destructive/5 p-2 mb-3">
                  {t("settings.googleNotConfigured")}
                </p>
              )}
              <div className="border border-border divide-y divide-border">
                {[["drive", HardDrive], ["gmail", Mail]].map(([service, Icon]) => {
                  const status = google.services?.[service] || { connected: false, email: null };
                  return (
                    <div key={service} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 bg-secondary">
                      <div className="flex items-center gap-2 min-w-0">
                        <Icon className="h-4 w-4 text-brand shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium">{t(`settings.googleService_${service}`)}</p>
                          <p className="text-xs text-muted-foreground truncate" data-testid={`google-${service}-status`}>
                            {status.connected ? t("settings.googleConnectedAs", { email: status.email }) : t("settings.googleNotConnected")}
                          </p>
                        </div>
                      </div>
                      {service === "gmail" && status.connected && (
                        <Button type="button" size="sm" variant="outline" className="rounded-none" onClick={sendGmailTest} disabled={gmailTesting} data-testid="gmail-test-button">
                          {gmailTesting ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1" />}
                          {t("settings.gmailSendTest")}
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="flex flex-wrap justify-end gap-2 mt-4">
                {google.connected && (
                  <Button type="button" size="sm" variant="ghost" className="rounded-none text-destructive" onClick={disconnectGoogle} data-testid="google-disconnect-button">
                    <Trash2 className="h-3.5 w-3.5 mr-1" />{t("settings.googleDisconnect")}
                  </Button>
                )}
                <Button type="button" size="sm" variant="outline" className="rounded-none" onClick={connectGoogle} disabled={googleConnecting || !googleConfigured} data-testid="google-connect-button">
                  {googleConnecting ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : (google.connected ? <RefreshCw className="h-3.5 w-3.5 mr-1" /> : <Plug className="h-3.5 w-3.5 mr-1" />)}
                  {googleConnecting ? t("settings.googleConnecting") : (google.connected ? t("settings.googleReconnect") : t("settings.googleConnect"))}
                </Button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="whitelist" className="mt-4 space-y-4">
            <div className="grid-panel p-5 flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[200px]">
                <Label className="font-mono text-[11px] uppercase text-muted-foreground">{t("settings.emailLabel")}</Label>
                <Input value={newEmail} onChange={(e) => setNewEmail(e.target.value)} className="rounded-none mt-1.5" data-testid="allowed-email-input" placeholder="user@example.com" />
              </div>
              <div>
                <Label className="font-mono text-[11px] uppercase text-muted-foreground">{t("settings.roleLabel")}</Label>
                <Select value={newRole} onValueChange={setNewRole}>
                  <SelectTrigger className="rounded-none mt-1.5 w-36" data-testid="allowed-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="admin">admin</SelectItem><SelectItem value="operator">operator</SelectItem><SelectItem value="viewer">viewer</SelectItem></SelectContent>
                </Select>
              </div>
              <Button className="rounded-none uppercase bg-[#0A0A0A] hover:bg-[#0052FF]" onClick={addAllowed} data-testid="add-allowed-button"><Plus className="h-4 w-4 mr-1" />{t("settings.addBtn")}</Button>
            </div>
            <div className="grid-panel overflow-x-auto">
              <table className="w-full text-sm min-w-[600px]">
                <thead><tr className="text-left font-mono text-[10px] uppercase text-muted-foreground border-b border-border"><th className="px-5 py-3">{t("settings.thEmail")}</th><th className="px-5 py-3">{t("settings.thRole")}</th><th className="px-5 py-3">{t("settings.thAddedBy")}</th><th className="px-5 py-3 text-right"></th></tr></thead>
                <tbody>
                  {allowed.map((a) => (
                    <tr key={a.email} className="data-grid-row" data-testid={`allowed-row-${a.email}`}>
                      <td className="px-5 py-3 font-mono text-xs">{a.email}</td>
                      <td className="px-5 py-3">{a.role}</td>
                      <td className="px-5 py-3 font-mono text-xs text-muted-foreground">{a.added_by}</td>
                      <td className="px-5 py-3 text-right"><Button size="sm" variant="ghost" className="rounded-none text-destructive" onClick={() => removeAllowed(a.email)} data-testid={`remove-allowed-${a.email}`}><Trash2 className="h-3.5 w-3.5" /></Button></td>
                    </tr>
                  ))}
                  {allowed.length === 0 && <tr><td colSpan="4" className="px-5 py-8 text-center text-muted-foreground">{t("settings.noWhitelist")}</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="health" className="mt-4 space-y-4">
            <div className="grid-panel p-5" data-testid="egress-ip-card">
              <div className="flex items-center gap-2 mb-1">
                <Globe className="h-4 w-4 text-brand" />
                <h3 className="font-display font-bold">{t("settings.egressTitle")}</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-4 max-w-2xl">{t("settings.egressDesc")}</p>
              <div className="flex flex-wrap items-center gap-3 border border-border bg-secondary px-4 py-3">
                <code className="font-mono text-base font-semibold flex-1 min-w-0 break-all" data-testid="egress-ip-value">
                  {egressLoading ? t("settings.egressLoading") : (egress?.ip || t("settings.egressUnknown"))}
                </code>
                <div className="flex items-center gap-2 shrink-0">
                  <Button type="button" size="sm" variant="outline" className="rounded-none" onClick={copyEgress} disabled={!egress?.ip} data-testid="egress-copy-button">
                    <Copy className="h-3.5 w-3.5 mr-1" />{t("settings.egressCopy")}
                  </Button>
                  <Button type="button" size="sm" variant="ghost" className="rounded-none" onClick={loadEgress} disabled={egressLoading} data-testid="egress-refresh-button">
                    {egressLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                  </Button>
                </div>
              </div>
            </div>

            <div className="grid-panel p-5">
              <div className="flex items-center gap-2 mb-4"><Activity className="h-4 w-4" /><h3 className="font-display font-bold">{t("settings.sysHealth")}</h3>
                <StatusBadge status={health?.status === "healthy" ? "valid" : "invalid"} label={health?.status} /></div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border">
                {health && Object.entries(health.checks || {}).map(([k, v]) => (
                  <div key={k} className="bg-card px-4 py-3">
                    <p className="font-mono text-[10px] uppercase text-muted-foreground">{k}</p>
                    <p className="text-sm font-medium mt-1 break-all">{String(v)}</p>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="notifications" className="mt-4 space-y-4">
            <div className="grid-panel overflow-x-auto">
              <table className="w-full text-sm min-w-[600px]">
                <thead><tr className="text-left font-mono text-[10px] uppercase text-muted-foreground border-b border-border"><th className="px-5 py-3">{t("settings.thEvent")}</th><th className="px-5 py-3">{t("settings.thChannel")}</th><th className="px-5 py-3">{t("settings.thStatus")}</th><th className="px-5 py-3">{t("settings.thWhen")}</th></tr></thead>
                <tbody>
                  {notifs.map((n) => (
                    <tr key={n.id} className="data-grid-row">
                      <td className="px-5 py-3 font-mono text-xs">{n.event}</td>
                      <td className="px-5 py-3">{n.channel}</td>
                      <td className="px-5 py-3 font-mono text-xs">{n.status}{n.error ? ` · ${n.error}` : ""}</td>
                      <td className="px-5 py-3 font-mono text-xs text-muted-foreground">{fmtDate(n.created_at)}</td>
                    </tr>
                  ))}
                  {notifs.length === 0 && <tr><td colSpan="4" className="px-5 py-8 text-center text-muted-foreground">{t("settings.noNotifs")}</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="audit" className="mt-4">
            <div className="grid-panel overflow-x-auto">
              <table className="w-full text-sm min-w-[600px]">
                <thead><tr className="text-left font-mono text-[10px] uppercase text-muted-foreground border-b border-border"><th className="px-5 py-3">{t("settings.thActor")}</th><th className="px-5 py-3">{t("settings.thAction")}</th><th className="px-5 py-3">{t("settings.thTarget")}</th><th className="px-5 py-3">{t("settings.thWhen")}</th></tr></thead>
                <tbody>
                  {audit.map((a) => (
                    <tr key={a.id} className="data-grid-row">
                      <td className="px-5 py-3 font-mono text-xs">{a.actor_email}</td>
                      <td className="px-5 py-3 font-mono text-xs">{a.action}</td>
                      <td className="px-5 py-3 font-mono text-xs text-muted-foreground">{a.target_type} {a.target_id}</td>
                      <td className="px-5 py-3 font-mono text-xs text-muted-foreground">{fmtDate(a.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
          </>}
        </Tabs>
      </div>
    </div>
  );
}
